import { useEffect, useRef } from 'react';

const useAutoPlay = ({ autoPlay, src, muted = true }: { autoPlay: boolean; src: string; muted?: boolean }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasRetriedRef = useRef(false);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!autoPlay) return;
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      const el = videoRef.current;
      if (!el) return;
      // if already started or progressed, avoid re-trigger which can cause restart on iOS
      if (!el.paused || el.currentTime > 0.05 || hasStartedRef.current) {
        hasStartedRef.current = true;
        return;
      }
      // ensure muted before play for mobile autoplay policies
      el.muted = true;
      el.play()
        .then(() => {
          hasStartedRef.current = true;
          el.muted = muted;
        })
        .catch(() => {});
    };

    const onCanPlay = () => {
      tryPlay();
    };

    video.addEventListener('canplay', onCanPlay, { once: true } as AddEventListenerOptions);

    // delayed single retry to mitigate race conditions
    const retryTimer = window.setTimeout(() => {
      if (!hasRetriedRef.current) {
        hasRetriedRef.current = true;
        tryPlay();
      }
    }, 200);

    // initial attempt immediately
    tryPlay();

    return () => {
      video.removeEventListener('canplay', onCanPlay as EventListener);
      window.clearTimeout(retryTimer);
    };
  }, [autoPlay, muted, src]);

  return {
    ref: videoRef
  };
};

export default useAutoPlay;
